import { useMemo, useCallback, useState } from 'react';
import { AWARE_CONFIG } from '../consts/aware';
import { useCreateApiKeyMutation } from '../network/mutations/useCreateApiKeyMutation';
import { QUERY_PARAMS } from '../routes/consts';
import { decodeReturnUrl, extractStateFromReturnUrl } from '../utils/aware';
import { getQueryParam, isAwareFlowOn } from '../utils/url';

/**
 * A streamlined hook to manage the entire aware authentication flow.
 * Conditionally uses either the aware mutation (Auth V1) or direct API key creation (Auth V2).
 */
export const useAwareFlow = () => {
  const [hasError, setHasError] = useState(false);

  const apiKeyMutation = useCreateApiKeyMutation({
    name: AWARE_CONFIG.API_KEY_NAME,
  });
  const {
    mutate: createApiKey,
    isPending,
    isSuccess,
    isError,
  } = apiKeyMutation;

  const returnURL = decodeReturnUrl(getQueryParam(QUERY_PARAMS.RETURN_URL));
  const state = extractStateFromReturnUrl(returnURL);

  const isAwareMode = useMemo(() => isAwareFlowOn(), []);
  const hasValidParams = useMemo(
    () => Boolean(state && returnURL),
    [state, returnURL],
  );
  const isAware = useMemo(
    () => isAwareMode && hasValidParams,
    [isAwareMode, hasValidParams],
  );

  const handleAwareFlow = useCallback(() => {
    if (isAwareMode && !hasValidParams) {
      setHasError(true);

      return;
    }

    // TODO: Uncomment this as soon as the ui-platform code freeze is over and we know the origin
    // https://linear.app/qodo/issue/PLT-1711/add-portal-as-oauth-provider-for-external-websites
    // if (!isOriginValid) {
    //   setHasError(true);
    //   return;
    // }

    setHasError(false);
    createApiKey();
  }, [isAwareMode, hasValidParams, createApiKey]);

  const apiKey = apiKeyMutation.data?.key;

  return {
    isAwareMode,
    isPending: isPending && !hasError,
    isSuccess,
    isError: hasError || isError,
    apiKey,
    awareData: { isAware, returnURL },
    handleAwareFlow,
  };
};
